import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
    AlertTriangle,
    Clock,
    ChevronRight,
    Flame,
    Calendar as CalendarIcon,
    Building2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { getUpcomingAlerts } from "@/lib/api";
import { fmtDate, fmtEUR } from "@/lib/format";

const TONE = {
    overdue: {
        wrap: "bg-gradient-to-br from-red-600 via-red-500 to-rose-500 text-white border-red-300",
        icon: Flame,
        chip: "bg-white/15 text-white border-white/30",
        label: "VENCIDA",
        accent: "text-white",
    },
    today: {
        wrap: "bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white border-amber-300",
        icon: Flame,
        chip: "bg-white/15 text-white border-white/30",
        label: "HOY",
        accent: "text-white",
    },
    week: {
        wrap: "bg-gradient-to-br from-amber-50 via-orange-50 to-white text-ink-950 border-amber-300",
        icon: AlertTriangle,
        chip: "bg-amber-100 text-amber-800 border-amber-300",
        label: "URGENTE",
        accent: "text-amber-700",
    },
    upcoming: {
        wrap: "bg-white text-ink-950 border-slate-200",
        icon: Clock,
        chip: "bg-slate-100 text-slate-700 border-slate-200",
        label: "PRÓXIMA",
        accent: "text-brand-600",
    },
};

const toneFor = (daysLeft) => {
    if (daysLeft < 0) return "overdue";
    if (daysLeft === 0) return "today";
    if (daysLeft <= 7) return "week";
    return "upcoming";
};

export const DeadlineAlerts = () => {
    const [data, setData] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        getUpcomingAlerts(14)
            .then(setData)
            .catch(() => {});
    }, []);

    if (!data || dismissed) return null;
    // Mix in tracking items (commitments + tasks) with the existing tender deadlines.
    // Tender items carry kind: "tender", tracking items kind: "commitment" or "task".
    const tenderItems = [
        ...(data.overdue || []).map((x) => ({ ...x, kind: x.kind || "tender" })),
        ...(data.upcoming || []).map((x) => ({ ...x, kind: x.kind || "tender" })),
    ];
    const trackingItems = [
        ...(data.tracking_overdue || []),
        ...(data.tracking_upcoming || []),
    ];
    const items = [...tenderItems, ...trackingItems].sort(
        (a, b) => (a.days_left ?? 0) - (b.days_left ?? 0),
    );
    if (!items.length) return null;

    const visible = items.slice(0, 4);
    const overdueCount =
        (data.overdue?.length || 0) + (data.tracking_overdue?.length || 0);
    const upcomingCount =
        (data.upcoming?.length || 0) + (data.tracking_upcoming?.length || 0);

    return (
        <motion.div
            className="mb-8"
            data-testid="deadline-alerts"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
            {/* Header */}
            <div className="flex items-end justify-between mb-4 gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-600 mb-1.5 flex items-center gap-2">
                        <span className="inline-block w-6 h-px bg-amber-600" />
                        Plazos críticos
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl font-black tracking-tight text-ink-950 leading-none">
                        {overdueCount > 0 ? (
                            <>
                                <span className="text-red-600">{overdueCount}</span> vencida
                                {overdueCount > 1 ? "s" : ""}
                                {upcomingCount > 0 && (
                                    <>
                                        {" · "}
                                        <span className="text-amber-600">{upcomingCount}</span>{" "}
                                        en 14 días
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <span className="text-amber-600">{upcomingCount}</span> próxima
                                {upcomingCount > 1 ? "s" : ""} en 14 días
                            </>
                        )}
                    </h2>
                </div>
                {items.length > 4 && (
                    <Link
                        to="/app/licitaciones"
                        className="text-xs font-bold text-ink-500 hover:text-brand-600 flex items-center gap-1 rounded-xl px-3 py-1.5 hover:bg-brand-50 transition-colors"
                        data-testid="alerts-see-all"
                    >
                        Ver todas ({items.length})
                        <ChevronRight size={12} />
                    </Link>
                )}
            </div>

            {/* Cards grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {visible.map((t, i) => {
                    const tone = TONE[toneFor(t.days_left)];
                    const Icon = tone.icon;
                    const absDays = Math.abs(t.days_left);
                    const overdue = t.days_left < 0;
                    const dayText = overdue
                        ? `-${absDays}`
                        : t.days_left === 0
                          ? "HOY"
                          : `${absDays}`;
                    const subText = overdue
                        ? `día${absDays > 1 ? "s" : ""} vencido${absDays > 1 ? "s" : ""}`
                        : t.days_left === 0
                          ? "Cierra a medianoche"
                          : `día${absDays > 1 ? "s" : ""} restantes`;
                    const isCommitment = t.kind === "commitment";
                    const isTask = t.kind === "task";
                    const isTracking = isCommitment || isTask;
                    const targetUrl = isTracking
                        ? "/app/seguimiento"
                        : `/app/licitacion/${t.id}`;
                    const kindLabel = isCommitment
                        ? "COMPROMISO"
                        : isTask
                          ? "TAREA"
                          : null;
                    return (
                        <motion.div
                            key={`${t.kind || "tender"}-${t.id}`}
                            initial={{ opacity: 0, y: 12, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            transition={{
                                duration: 0.45,
                                delay: i * 0.08,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                        >
                            <Link
                                to={targetUrl}
                                className={`relative block p-5 border-2 rounded-2xl overflow-hidden group hover:-translate-y-1 transition-all duration-300 hover:shadow-pop-lg ${tone.wrap}`}
                                data-testid={`alert-item-${t.kind || "tender"}-${t.id}`}
                            >
                                {/* Decorative glow on dark cards */}
                                {(toneFor(t.days_left) === "overdue" ||
                                    toneFor(t.days_left) === "today") && (
                                    <>
                                        <div className="absolute -top-12 -right-12 w-32 h-32 bg-white/20 rounded-full blur-3xl pointer-events-none" />
                                        <div className="absolute -bottom-10 -left-10 w-28 h-28 bg-white/10 rounded-full blur-2xl pointer-events-none" />
                                    </>
                                )}
                                <div className="relative">
                                    {/* Top row: chips + icon */}
                                    <div className="flex items-start justify-between mb-3 gap-2">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                            <span
                                                className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-md border ${tone.chip}`}
                                            >
                                                <Icon size={10} />
                                                {tone.label}
                                            </span>
                                            {kindLabel && (
                                                <span
                                                    className={`inline-flex items-center text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-md border ${tone.chip}`}
                                                >
                                                    {kindLabel}
                                                </span>
                                            )}
                                        </div>
                                        <ChevronRight
                                            size={16}
                                            className="opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all"
                                        />
                                    </div>

                                    {/* Massive day counter */}
                                    <div
                                        className={`font-display font-black tracking-tight leading-none ${
                                            dayText === "HOY"
                                                ? "text-5xl"
                                                : "text-6xl md:text-7xl"
                                        }`}
                                    >
                                        {dayText}
                                    </div>
                                    <div className="text-[11px] font-bold uppercase tracking-wider opacity-80 mt-1.5">
                                        {subText}
                                    </div>

                                    {/* Divider */}
                                    <div
                                        className={`my-4 h-px ${
                                            toneFor(t.days_left) === "overdue" ||
                                            toneFor(t.days_left) === "today"
                                                ? "bg-white/20"
                                                : "bg-slate-200"
                                        }`}
                                    />

                                    {/* Body */}
                                    <div className="space-y-1.5">
                                        <div className="text-sm font-bold leading-snug line-clamp-2">
                                            {t.title}
                                        </div>
                                        {isTracking && t.tender_title && (
                                            <div className="text-[11px] opacity-75 flex items-center gap-1 truncate">
                                                <Building2 size={11} />
                                                <span className="truncate">
                                                    Licitación: {t.tender_title}
                                                </span>
                                            </div>
                                        )}
                                        <div className="text-[11px] opacity-75 flex items-center gap-1">
                                            <CalendarIcon size={11} />
                                            {fmtDate(t.deadline)}
                                        </div>
                                        {!isTracking && t.contracting_authority && (
                                            <div className="text-[11px] opacity-75 flex items-center gap-1 truncate">
                                                <Building2 size={11} />
                                                <span className="truncate">
                                                    {t.contracting_authority}
                                                </span>
                                            </div>
                                        )}
                                        {!isTracking && typeof t.budget === "number" && t.budget > 0 && (
                                            <div className="text-xs font-mono font-bold pt-1">
                                                {fmtEUR(t.budget)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Link>
                        </motion.div>
                    );
                })}
            </div>
        </motion.div>
    );
};

export default DeadlineAlerts;
